<template>
  <BoxDetailsDrawerSectionCard>
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <DeepAppLogo
          class="deepvalidator-logo"
          logo="deepvalidator"
          :width="100"
        />

        <VBtnPrimary
          :disabled="loading"
          :loading
          name="btn-box-details-drawer-validator"
          variant="text"
          @click="handleProcess"
        >
          {{
            loading
              ? t(
                  'box_details.details_drawer.sections.deep_validator.actions.processing',
                )
              : result
                ? t(
                    'box_details.details_drawer.sections.deep_validator.actions.re_validate',
                  )
                : t(
                    'box_details.details_drawer.sections.deep_validator.actions.validate',
                  )
          }}
        </VBtnPrimary>
      </div>
    </template>
    <template #content>
      <div v-if="error">
        {{ error }}
      </div>

      <v-container v-else-if="result" class="pa-0" fluid>
        <div class="text-medium-emphasis text-body-medium">
          {{
            t(
              'box_details.details_drawer.sections.deep_validator.validation_results',
            )
          }}
        </div>

        <template v-if="showSignaturesCount">
          <div v-if="showSignaturesCount" class="my-4">
            <div class="text-medium-emphasis text-body-medium">
              {{
                t(
                  'box_details.details_drawer.sections.deep_validator.nr_of_signatures',
                )
              }}
            </div>

            <div
              v-for="signatureStatus in signatureStatusDisplay"
              :key="signatureStatus.text"
              class="text-body-medium"
            >
              {{ signatureStatus?.text }}
            </div>
          </div>

          <div class="my-4">
            <CardExpandable
              v-for="signature in result.signatures"
              :key="signature.signerName"
            >
              <template #title>
                <v-icon
                  v-if="signature.timestampResultIndication"
                  :color="
                    TIMESTAMP_RESULT_INDICATION_MAP[
                      signature.timestampResultIndication
                    ].color
                  "
                  :icon="
                    TIMESTAMP_RESULT_INDICATION_MAP[
                      signature.timestampResultIndication
                    ].icon
                  "
                />

                <span class="text-body-medium ml-1 text-truncate">
                  {{ signature.signerName }}
                </span>
                <v-spacer />
              </template>
              <template #content>
                <DeepListKeyValue
                  :items="[
                    {
                      key: t(
                        'box_details.details_drawer.sections.deep_validator.signature_type',
                      ),
                      value: signature.signatureQualification
                        ? te(
                            `box_details.details_drawer.sections.deep_validator.signature_types.${signature.signatureQualification}`,
                          )
                          ? t(
                              `box_details.details_drawer.sections.deep_validator.signature_types.${signature.signatureQualification}`,
                            )
                          : signature.signatureQualification
                        : '-',
                    },
                    {
                      key: t(
                        'box_details.details_drawer.sections.deep_validator.jurisdiction',
                      ),
                      value: signature.jurisdiction
                        ? te(
                            `box_details.details_drawer.sections.deep_validator.jurisdictions.${signature.jurisdiction}`,
                          )
                          ? t(
                              `box_details.details_drawer.sections.deep_validator.jurisdictions.${signature.jurisdiction}`,
                            )
                          : signature.jurisdiction
                        : '-',
                    },
                    {
                      key: t(
                        'box_details.details_drawer.sections.deep_validator.time_of_signature',
                      ),
                      value: signature.signatureTimestamp
                        ? formatDateTimeByDefaultFormat(
                            signature.signatureTimestamp,
                          )
                        : '-',
                    },
                    {
                      key: t(
                        'box_details.details_drawer.sections.deep_validator.validation_result_indication',
                      ),
                      value: signature.validationResultIndication
                        ? te(
                            `box_details.details_drawer.sections.deep_validator.validation_result_indications.${signature.validationResultIndication}`,
                          )
                          ? t(
                              `box_details.details_drawer.sections.deep_validator.validation_result_indications.${signature.validationResultIndication}`,
                            )
                          : signature.validationResultIndication
                        : '-',
                    },
                  ]"
                />
              </template>
            </CardExpandable>
          </div>
        </template>
        <template v-else>
          <span class="text-body-medium">
            {{
              t(
                'box_details.details_drawer.sections.deep_validator.no_signatures_found',
              )
            }}
          </span>
        </template>
      </v-container>
    </template>
  </BoxDetailsDrawerSectionCard>
</template>

<script setup lang="ts">
import {
  SignatureValidationResultRepresentationTimestampResultIndication,
  ValidationResultRepresentation,
} from '@deepcloud/api-deep-validator'
import { isHttpClientError } from '@deepcloud/api-http-client'
import { DeepAppLogo, DeepListKeyValue } from '@deepcloud/deep-ui-lib'
import { formatDateTimeByDefaultFormat } from '@deepcloud/deep-ui-utils/date'
import { useApi } from '@/composables/api.ts'
import BoxDetailsDrawerSectionCard from '@/components/box/details-drawer/BoxDetailsDrawerSectionCard.vue'
import CardExpandable from '@/components/cards/CardExpandable.vue'

const props = defineProps({
  nodeId: {
    type: String,
    required: true,
  },
})

const { t, te } = useI18n()
const { deepBoxAPI, deepValidatorAPI } = useApi()

const loading = ref(false)
const error = ref<string | null>(null)
const result = ref<ValidationResultRepresentation>()

watch(
  () => props.nodeId,
  () => {
    result.value = undefined
  },
  { immediate: true },
)

async function getDownloadUrl() {
  try {
    const { data } = await deepBoxAPI.value.nodesDownloadUrl.get(props.nodeId)
    return data
  } catch (e) {
    console.error(e)
  }
}

async function downloadFile(url: string) {
  const res = await fetch(url)

  if (!res.ok) {
    throw new Error(`Download failed: ${res.status}`)
  }

  return res.blob()
}

async function uploadFile(file: Blob) {
  try {
    const { data } = await deepValidatorAPI.value.validation.create({
      document: file as File,
      timezone: 'Europe/Zurich',
      returnJsonReport: true,
    })

    return data
  } catch (e) {
    console.error(e)
  }
}

async function handleProcess() {
  loading.value = true
  error.value = null
  result.value = undefined

  try {
    const url = await getDownloadUrl()

    if (!url) return

    const fileBlob = await downloadFile(url)

    const uploadResult = await uploadFile(fileBlob)

    if (uploadResult) {
      result.value = uploadResult
    }
  } catch (err: unknown) {
    if (isHttpClientError(err)) {
      error.value = err.message || 'Something went wrong'
    }
  } finally {
    loading.value = false
  }
}

type SignatureStatus = 'valid' | 'unsupported' | 'indeterminate' | 'invalid'

interface SignatureCount {
  valid: number
  unsupported: number
  indeterminate: number
  invalid: number
}

interface StatusInfo {
  icon: string
  color: string
  text: string
}

// Mapping for each status
const statusMap: Record<SignatureStatus, StatusInfo> = {
  valid: {
    icon: 'fas fa-shield-check',
    color: 'success',
    text: 'box_details.details_drawer.sections.deep_validator.n_signature_status.valid',
  },
  unsupported: {
    icon: 'fa fa-circle-question',
    color: 'gray',
    text: 'box_details.details_drawer.sections.deep_validator.n_signature_status.unsupported',
  },
  indeterminate: {
    icon: 'fas fa-shield-exclamation',
    color: 'orange',
    text: 'box_details.details_drawer.sections.deep_validator.n_signature_status.indeterminate',
  },
  invalid: {
    icon: 'fas fa-shield-xmark',
    color: 'error',
    text: 'box_details.details_drawer.sections.deep_validator.n_signature_status.invalid',
  },
}

/**
 * Returns an array of status objects for all counts > 0
 */
function getSignatureStatusDisplay(count: SignatureCount): StatusInfo[] {
  const result: StatusInfo[] = []

  for (const key of Object.keys(count) as SignatureStatus[]) {
    const value = count[key]
    if (value > 0) {
      result.push({
        ...statusMap[key],
        text: t(statusMap[key].text, value),
      })
    }
  }

  return result
}

const signatureStatusDisplay = computed(() =>
  getSignatureStatusDisplay(
    result.value?.summary?.signatureCount as unknown as SignatureCount,
  ),
)

const TIMESTAMP_RESULT_INDICATION_MAP: Record<
  SignatureValidationResultRepresentationTimestampResultIndication,
  { icon: string; color: string }
> = {
  PASSED: {
    icon: 'far fa-circle-check',
    color: 'success',
  },
  INDETERMINATE: {
    icon: 'far fa-triangle-exclamation',
    color: 'orange',
  },
  FAILED: {
    icon: 'far fa-circle-xmark',
    color: 'error',
  },
}

const showSignaturesCount = computed(() => {
  const counts = result.value?.summary?.signatureCount

  return !!counts && Object.values(counts).some((v) => v > 0)
})
</script>

<style lang="scss" scoped>
.deepvalidator-logo {
  :deep(.DeepValidatorLogo) {
    fill: #18181b !important;
  }
}
</style>
